import { Box } from '@mui/material';
import { useRef } from 'react';
import ProductGridInput from '../../../../../../components/ProductGridInput/ProductGridInput';
import { CatalogProductsTestIds } from '../../../../../../utils/testIds/CatalogProducts.testIds';
import OrderProductPriceV2 from '../../../../../orders/products/product/components/OrderProductPriceV2';
import { AvailabilityEnum } from '../../../../../products/ProductTypes';
import ProductDetailsAddOrderButtonV2 from '../../../../../products/products-details-v2/ProductDetailsAddOrderButtonV2';
import { CatalogSectionKit } from '../../../interfaces/catalogProductList.types';
import { RowCard } from '../../catalogProductsListV2.types';
import { useCatalogDraggableItem } from '../../hooks/useCatalogDraggableItem';
import useCatalogKitListRow from '../hooks/useCatalogKitListRow';
import { measureCatalogListDragRect } from '../utils/catalogListDragRect.utils';
import { CATALOG_LIST_RIGHT_BLOCK_GAP } from '../utils/catalogListRow.consts';
import CatalogListKitChip from './CatalogListKitChip';
import CatalogProductListRow, { CatalogListRowAdjacentSelection } from './CatalogProductListRow';
import { CatalogListRowDragConfig } from './CatalogProductListRowConnected';
import CatalogProductListRowLeftBlock from './CatalogProductListRowLeftBlock';

interface CatalogKitListRowProps {
  catalogId: string;
  card: Extract<RowCard, { kind: 'item' }>;
  kit: CatalogSectionKit;
  isStacked: boolean;
  adjacentSelection: CatalogListRowAdjacentSelection;
  drag?: CatalogListRowDragConfig;
}

const CatalogKitListRow = ({
  catalogId,
  card,
  kit,
  isStacked,
  adjacentSelection,
  drag,
}: CatalogKitListRowProps) => {
  const leftBlockRef = useRef<HTMLDivElement | null>(null);
  const row = useCatalogKitListRow({ catalogId, card, kit });

  const leftBlock = { isStacked, ...row.leftBlock };

  const { attributes, listeners, setActivatorNodeRef, setNodeRef } = useCatalogDraggableItem({
    id: card.esProduct.id,
    isDraggingDisabled: !drag || drag.isDraggingDisabled,
    data: {
      listRow: {
        leftBlock,
        measureRect: () => measureCatalogListDragRect(leftBlockRef.current),
      },
    },
  });

  const setActivatorRef = (node: HTMLDivElement | null) => {
    leftBlockRef.current = node;
    setActivatorNodeRef(node);
  };

  const addButton = (
    <ProductDetailsAddOrderButtonV2
      disabled={row.isAddButtonDisabled}
      gridButtonStatus={row.gridButtonStatus}
      tooltipMessage={row.tooltipMessage}
      testId={CatalogProductsTestIds.PRODUCT_ADD_BUTTON}
      onAdd={row.onAdd}
      onRemove={row.onRemove}
    />
  );

  const grid = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: `${CATALOG_LIST_RIGHT_BLOCK_GAP}px`,
      }}
    >
      <ProductGridInput
        showStock
        gridItem={row.gridItem}
        inputRef={row.inputRef}
        stock={row.stock}
        maxValue={row.maxValue}
        disabled={row.isDisabled}
        productColorAvailability={AvailabilityEnum.EMPTY}
        onChangeGrid={({ value }) => row.onChangeQuantity(value)}
      />
      {isStacked && addButton}
    </Box>
  );

  const price = row.price.isHidden ? null : (
    <OrderProductPriceV2
      grid={row.price.grid}
      partsQuantity={row.price.partsQuantity}
      total={row.price.total}
      showTotal={row.price.showTotal}
      hidePricesTooltip={row.price.hidePricesTooltip}
    />
  );

  return (
    <CatalogProductListRow
      isStacked={isStacked}
      rowRef={setNodeRef}
      selection={row.selection && { ...row.selection, ...adjacentSelection }}
      addButton={addButton}
      dragProps={drag && { attributes, listeners, activatorRef: setActivatorRef }}
      combinations={<CatalogListKitChip />}
      left={<CatalogProductListRowLeftBlock {...leftBlock} />}
      grid={grid}
      price={price}
    />
  );
};

export default CatalogKitListRow;
